import React, { useCallback, useRef } from 'react'
import ReactToPrint from 'react-to-print'
import { Heading3 } from '@/components/ui/typography'
import { useApiClient } from '@/utils/api/ApiClient'
import { DocumentApiClient } from '@/utils/api/documentApi'
import { PrinterFilled } from 'components/Icon'
import { Spinner } from 'components/Spinner'
import styled from 'styled-components'
import { useFetchOne } from 'utils/api/useFetch'
import { formatLocalDateUTC } from 'utils/date'
import { normalizeField } from 'utils/string.utils'

interface IProps {
  reportId: string
  client: string
}

interface Inverse {
  inverse?: boolean
}

interface DocumentDataReport {
  Dynamic: { [key: string]: string }
  Extraction: { Data: { [key: string]: string } }
  Verification: {
    Checks: {
      [key: string]: { Description: string; Result: 'Skipped' | 'Passed' }
    }
    Validations: {
      [key: string]: { Description: string; Result: 'Passed' | 'Skipped' }
    }
  }
}

export const RemoveIcon: React.FC<Inverse> = () => (
  <svg className='svg-icon' viewBox='0 0 20 20'>
    <path
      fill='#1b2337'
      d='M15.898,4.045c-0.271-0.272-0.713-0.272-0.986,0l-4.71,4.711L5.493,4.045c-0.272-0.272-0.714-0.272-0.986,0s-0.272,0.714,0,0.986l4.709,4.711l-4.71,4.711c-0.272,0.271-0.272,0.713,0,0.986c0.136,0.136,0.314,0.203,0.492,0.203c0.179,0,0.357-0.067,0.493-0.203l4.711-4.711l4.71,4.711c0.137,0.136,0.314,0.203,0.494,0.203c0.178,0,0.355-0.067,0.492-0.203c0.273-0.273,0.273-0.715,0-0.986l-4.711-4.711l4.711-4.711C16.172,4.759,16.172,4.317,15.898,4.045z'
    />
  </svg>
)

export const CheckmarkIcon: React.FC = () => (
  <svg xmlns='http://www.w3.org/2000/svg' width='8' height='8' viewBox='0 0 24 24' fill={'#00be2c'}>
    <path d='M20.285 2l-11.285 11.567-5.286-5.011-3.714 3.716 9 8.728 15-15.285z' />
  </svg>
)

const Wrapper = styled.div`
  .print {
    display: flex;
    margin-right: 2em;
    justify-content: flex-end;
  }
  svg {
    width: 24px;
    height: 24px;
    margin-left: auto;
  }
`
const ReportWrapper = styled.div`
  width: 95%;
  display: flex;
  flex-direction: column;
  margin: 1em;
  header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin: 2rem;
    span {
      font-weight: bold;
      margin: 0 0.25em;
    }
  }
  svg {
    height: 24px;
    width: 24px;
  }
  td,
  th {
    border-color: black;
    border-style: solid;
    border-width: 1px;
    text-align: center;
    font-family: Arial, sans-serif;
    font-size: 14px;
    overflow: hidden;
    padding: 10px 5px;
    word-break: normal;
    vertical-align: top;
    > span:last-child {
      font-weight: bold;
    }
  }
  td {
    text-align: left;
  }
  h3 {
    margin: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .document_data_report {
    margin-top: 2rem;
    border: 1px solid black;
    width: 100%;
    height: 100%;
  }
`

const ClientDocumentVerificationReportDetails: React.FC<IProps> = ({ reportId, client }) => {
  const documentApiClient = useApiClient(DocumentApiClient)
  const fetchData = useCallback(
    () => documentApiClient.getClientDocumentVerificationReportById(reportId),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [reportId]
  )
  const { data, isLoading } = useFetchOne(fetchData)
  const ref = useRef<HTMLDivElement | null>(null)
  if (isLoading) {
    return <Spinner />
  }
  if (!data) {
    return <Spinner />
  }

  // Get the datasource results from the API response
  const datasourceResults =
    data.verifyResult.record.datasourceResults.length > 0
      ? data.verifyResult.record.datasourceResults[0]
      : null

  if (!datasourceResults) {
    return <div>No report present...</div>
  }

  const isNewStructure = () => {
    return datasourceResults.datasourceFields && datasourceResults.datasourceFields.length > 0
  }

  const getAppendedFields = () => {
    return datasourceResults.appendedFields || []
  }

  const getDocumentDataField = (): DocumentDataReport | undefined => {
    const documentData = getAppendedFields().find((x) => x.fieldName === 'DocumentData')

    if (!documentData || !documentData.data) {
      return undefined
    }

    try {
      return JSON.parse(documentData.data as string)
    } catch (error) {
      console.error('Error parsing DocumentData:', error)
      return undefined
    }
  }

  const processFieldValue = (value: string | number | boolean) => {
    if (value === 'True' || value === true) {
      return <CheckmarkIcon />
    } else if (value === 'False' || value === false) {
      return <RemoveIcon />
    }
    return value
  }

  const documentDataField = getDocumentDataField()

  return (
    <Wrapper>
      <div className='print'>
        <ReactToPrint
          documentTitle={`${client} - ${data.verifyResult.record.recordStatus}`}
          trigger={() => <PrinterFilled />}
          content={() => ref.current}
        />
      </div>
      <ReportWrapper ref={ref}>
        <header>
          <div>
            Client: <span>{client}</span>
          </div>
          <div>
            Status: <span>{data.verifyResult.record.recordStatus}</span>
          </div>
        </header>
        <table>
          <tbody>
            <tr>
              <td>Country:</td>
              <td>
                <b>{data.verifyResult.countryCode}</b>
              </td>
            </tr>
            <tr>
              <td>Transaction Records ID:</td>
              <td>
                <b>{data.verifyResult.transactionID}</b>
              </td>
            </tr>
            <tr>
              <td>Transaction Date:</td>
              <td>
                <b>{formatLocalDateUTC(data.verifyResult.uploadedDt, true)}</b>
              </td>
            </tr>
            <tr>
              <td>Verification Rule:</td>
              <td>
                <b>{data.verifyResult.record.rule.ruleName}</b>
              </td>
            </tr>
            {data.verifyResult.record.rule.note && (
              <tr>
                <td>Rule Note:</td>
                <td>
                  <div style={{ whiteSpace: 'pre-line', padding: '10px' }}>
                    <div style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                      {data.verifyResult.record.rule.note.split('\n')[0]}
                    </div>
                    <ul style={{ listStyleType: 'disc', paddingLeft: '20px', margin: '5px 0' }}>
                      {data.verifyResult.record.rule.note
                        .replace(/\r\n/g, '\n')
                        .split('\n')
                        .slice(1)
                        .filter((line) => line.trim())
                        .map((line, idx) => {
                          // Format the rule logic line to make it more readable
                          const formattedLine = line
                            .replace(/AND/g, '⋀')
                            .replace(/OR/g, '⋁')
                            .replace(/NotNoMatch/g, '✓')
                            .trim()

                          return (
                            <li key={idx} style={{ margin: '5px 0' }}>
                              <span style={{ fontFamily: 'monospace' }}>{formattedLine}</span>
                            </li>
                          )
                        })}
                    </ul>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Heading3>Appended Information</Heading3>
        <table>
          <thead>
            <tr>
              <th>DataSource</th>
              <th>{datasourceResults.datasourceName}</th>
            </tr>
          </thead>
          <tbody>
            {isNewStructure()
              ? // new structure, combine datasourceFields with appendedFields
                [
                  ...datasourceResults.datasourceFields
                    .filter((field) => field.status !== 'missing')
                    .map((field) => ({
                      fieldName: field.fieldName,
                      displayValue: field.status === 'match' ? 'True' : 'False',
                    })),
                  ...getAppendedFields()
                    .filter((f) => f.fieldName !== 'DocumentData')
                    .map((field) => ({
                      fieldName: field.fieldName,
                      displayValue: field.data,
                    })),
                ].map((item, i) => (
                  <tr key={i}>
                    <td>{normalizeField(item.fieldName)}</td>
                    <td>{processFieldValue(item.displayValue)}</td>
                  </tr>
                ))
              : // use appendedFields only
                getAppendedFields()
                  .filter((f) => f.fieldName !== 'DocumentData')
                  .map((result, i) => (
                    <tr key={i}>
                      <td>{normalizeField(result.fieldName)}</td>
                      <td>{processFieldValue(result.data)}</td>
                    </tr>
                  ))}
          </tbody>
        </table>
        {documentDataField && (
          <div className='document_data_report' style={{}}>
            <Heading3>Document Data report</Heading3>
            <ul>
              <h4>Verification:</h4>
              <ul>
                <li>Checks:</li>
                {Object.entries(documentDataField.Verification.Checks).map((f, i) => (
                  <ul key={i}>
                    <li>{f[0]}</li>
                    <ul>
                      <span>
                        Description: <b>{f[1].Description}</b>
                      </span>
                      <br />
                      <span>
                        Result: <b>{f[1].Result}</b>
                      </span>
                    </ul>
                  </ul>
                ))}
                <li>Validations:</li>
                {Object.entries(documentDataField.Verification.Validations).map((f) => (
                  <ul key={f[0]}>
                    <li>{f[0]}</li>
                    <ul>
                      <span>
                        Description: <b>{f[1].Description}</b>
                      </span>
                      <br />
                      <span>
                        Result: <b>{f[1].Result}</b>
                      </span>
                    </ul>
                  </ul>
                ))}
              </ul>
              <h4>Extraction:</h4>
              <ul>
                <li>Data</li>
                {Object.entries(documentDataField.Extraction.Data).map((f) => (
                  <ul key={f[0]}>
                    <span>
                      {f[0]}: <b>{f[1]}</b>
                    </span>
                  </ul>
                ))}
              </ul>
              <h4>Dynamic:</h4>
              <ul>
                {Object.entries(documentDataField.Dynamic).map((f) => (
                  <ul key={f[0]}>
                    <span>
                      {f[0]}: <b>{f[1]}</b>
                    </span>
                  </ul>
                ))}
              </ul>
            </ul>
          </div>
        )}
      </ReportWrapper>
    </Wrapper>
  )
}

export default ClientDocumentVerificationReportDetails
